import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  approveAffiliatePartner,
  createAffiliatePartner,
  rejectAffiliatePartner,
  resendAffiliatePartnerPassword,
  resendAffiliatePartnerWelcomeEmail,
  updateAffiliatePartner,
} from "./api";
import { AffiliatePartnersKeys } from "./queries";
import type {
  AffiliatePartner,
  ApproveAffiliatePartnerBody,
  UpsertAffiliatePartnerBody,
} from "./types";

export function useCreateAffiliatePartnerMutation() {
  const qc = useQueryClient();

  return useMutation<AffiliatePartner, unknown, UpsertAffiliatePartnerBody>({
    mutationFn: createAffiliatePartner,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.all });
    },
  });
}

export function useUpdateAffiliatePartnerMutation() {
  const qc = useQueryClient();

  return useMutation<
    AffiliatePartner,
    unknown,
    { id: number | string; data: UpsertAffiliatePartnerBody }
  >({
    mutationFn: ({ id, data }) => updateAffiliatePartner(id, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.all });
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.detail(vars.id) });
    },
  });
}

export function useApproveAffiliatePartnerMutation() {
  const qc = useQueryClient();

  return useMutation<
    AffiliatePartner,
    unknown,
    { id: number | string; data: ApproveAffiliatePartnerBody }
  >({
    mutationFn: ({ id, data }) => approveAffiliatePartner(id, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.all });
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.detail(vars.id) });
    },
  });
}

export function useRejectAffiliatePartnerMutation() {
  const qc = useQueryClient();

  return useMutation<AffiliatePartner, unknown, number | string>({
    mutationFn: (id) => rejectAffiliatePartner(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.all });
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.detail(id) });
    },
  });
}

export function useResendAffiliatePartnerPasswordMutation() {
  const qc = useQueryClient();

  return useMutation<AffiliatePartner, unknown, number | string>({
    mutationFn: (id) => resendAffiliatePartnerPassword(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.all });
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.detail(id) });
    },
  });
}

export function useResendAffiliatePartnerWelcomeEmailMutation() {
  const qc = useQueryClient();

  return useMutation<AffiliatePartner, unknown, number | string>({
    mutationFn: (id) => resendAffiliatePartnerWelcomeEmail(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.all });
      qc.invalidateQueries({ queryKey: AffiliatePartnersKeys.detail(id) });
    },
  });
}
